/**
 * `<AutorouterSettings>` — the Settings → Autorouter panel (docs/design/AUTOROUTER-SPEC.md
 * §3.3).
 *
 * Holds: the master enable toggle, the active routing-prompt picker (10 shipped presets +
 * the user's saved custom prompts) with duplicate-and-customize CRUD, and the routing
 * activity list with a per-row "Send back to Inbox" undo.
 *
 * Reads settings values camelCase-first (the API maps the response through keysToCamel) with
 * a snake_case fallback; writes the camelCase patch through `onUpdate` (mapped to snake_case
 * by keysToSnake on the way out).
 *
 * U4: layout moved from .col/.row + inline styles onto the SettingsRow /
 * settings.css primitives; presets + activity queries gained loading/error
 * affordances (note: the activity query swallows fetch errors into an empty
 * list, so its error branch only fires if that ever changes).
 */

import { useMemo, useState } from 'react';
import type {
  AutorouterCustomPrompt,
  AutorouterPromptRef,
  SettingsView,
} from '@/types';
import { Icon } from '@/components';
import {
  useAutorouterActivityQuery,
  useAutorouterPresetsQuery,
  useUndoAutoroutingMutation,
} from '@/hooks';
import { SettingsCard } from './SettingsCard';
import { SettingsRow } from './SettingsRow';

interface AutorouterSettingsProps {
  settings: SettingsView;
  saving: boolean;
  onUpdate: (patch: Record<string, unknown>) => void;
}

/** Read a settings value by its camelCase key, falling back to the snake_case form. */
function readValue<T>(settings: SettingsView, camel: string, snake: string): T | undefined {
  const v = settings.values[camel] ?? settings.values[snake];
  return v as T | undefined;
}

export function AutorouterSettings({
  settings,
  saving,
  onUpdate,
}: AutorouterSettingsProps): JSX.Element {
  const presetsQuery = useAutorouterPresetsQuery();
  const activityQuery = useAutorouterActivityQuery(50);
  const undo = useUndoAutoroutingMutation();

  // --- current settings (default ON; default preset 'project_manager') -----------------
  const enabledRaw = readValue<unknown>(settings, 'autorouterEnabled', 'autorouter_enabled');
  const enabled = !(enabledRaw === false || enabledRaw === 'false');
  const active =
    readValue<AutorouterPromptRef>(settings, 'autorouterActivePrompt', 'autorouter_active_prompt') ??
    { kind: 'preset', id: 'project_manager' };
  const customs = useMemo<AutorouterCustomPrompt[]>(
    () =>
      readValue<AutorouterCustomPrompt[]>(
        settings,
        'autorouterCustomPrompts',
        'autorouter_custom_prompts',
      ) ?? [],
    [settings],
  );

  const presets = presetsQuery.data?.presets ?? [];

  // The body + editability of the currently-selected prompt.
  const selectedCustom = active.kind === 'custom' ? customs.find((c) => c.id === active.id) : undefined;
  const selectedPreset = active.kind === 'preset' ? presets.find((p) => p.id === active.id) : undefined;
  const selectedBody = selectedCustom?.body ?? selectedPreset?.body ?? '';

  // --- custom-prompt edit buffer (for the editable textarea) ---------------------------
  const [draftName, setDraftName] = useState<string | null>(null);
  const [draftBody, setDraftBody] = useState<string | null>(null);
  const editing = active.kind === 'custom' && draftBody !== null;

  function selectPrompt(ref: AutorouterPromptRef): void {
    setDraftName(null);
    setDraftBody(null);
    onUpdate({ autorouterActivePrompt: ref });
  }

  function duplicateAndCustomize(): void {
    const sourceName = selectedCustom?.name ?? selectedPreset?.name ?? 'Custom';
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `c-${Date.now()}`;
    const now = new Date().toISOString();
    const next: AutorouterCustomPrompt = {
      id,
      name: `${sourceName} (copy)`,
      body: selectedBody,
      createdAt: now,
      updatedAt: now,
    };
    onUpdate({ autorouterCustomPrompts: [...customs, next] });
    onUpdate({ autorouterActivePrompt: { kind: 'custom', id } });
    setDraftName(next.name);
    setDraftBody(next.body);
  }

  function beginEditCustom(): void {
    if (!selectedCustom) return;
    setDraftName(selectedCustom.name);
    setDraftBody(selectedCustom.body);
  }

  function saveCustom(): void {
    if (!selectedCustom || draftBody === null) return;
    const updated = customs.map((c) =>
      c.id === selectedCustom.id
        ? {
            ...c,
            name: (draftName ?? c.name).trim() || c.name,
            body: draftBody,
            updatedAt: new Date().toISOString(),
          }
        : c,
    );
    onUpdate({ autorouterCustomPrompts: updated });
    setDraftName(null);
    setDraftBody(null);
  }

  function deleteCustom(): void {
    if (!selectedCustom) return;
    onUpdate({ autorouterCustomPrompts: customs.filter((c) => c.id !== selectedCustom.id) });
    onUpdate({ autorouterActivePrompt: { kind: 'preset', id: 'project_manager' } });
    setDraftName(null);
    setDraftBody(null);
  }

  const selectValue = `${active.kind}:${active.id}`;

  return (
    <SettingsCard
      id="autorouter"
      title="Autorouter"
      desc="Files Inbox arrivals automatically using a routing prompt."
      fullWidth
    >
      <div className="settings-stack">
        {/* Master toggle */}
        <label className="settings-check">
          <input
            type="checkbox"
            checked={enabled}
            disabled={saving}
            onChange={(e) => onUpdate({ autorouterEnabled: e.target.checked })}
          />
          <span>
            <strong>{enabled ? 'Enabled' : 'Disabled'}</strong> — new Inbox notes are{' '}
            {enabled ? 'auto-filed' : 'left in the Inbox'}
          </span>
        </label>

        {/* Active prompt picker */}
        <SettingsRow
          stack
          htmlFor="autorouter-prompt"
          label="Routing prompt"
          hint={active.kind === 'preset' && selectedPreset ? selectedPreset.blurb : undefined}
        >
          <select
            id="autorouter-prompt"
            className="settings-select"
            value={selectValue}
            disabled={saving}
            onChange={(e) => {
              const [kind, ...rest] = e.target.value.split(':');
              selectPrompt({ kind: kind as 'preset' | 'custom', id: rest.join(':') });
            }}
            aria-label="Active routing prompt"
          >
            <optgroup label="Presets">
              {presets.map((p) => (
                <option key={p.id} value={`preset:${p.id}`}>
                  {p.name}
                </option>
              ))}
            </optgroup>
            {customs.length > 0 && (
              <optgroup label="My prompts">
                {customs.map((c) => (
                  <option key={c.id} value={`custom:${c.id}`}>
                    {c.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {presetsQuery.isLoading && <span className="settings-hint">Loading presets…</span>}
          {presetsQuery.isError && (
            <span className="settings-hint settings-hint--error">
              Couldn't load presets —{' '}
              <button
                type="button"
                className="btn btn--xs"
                onClick={() => void presetsQuery.refetch()}
              >
                retry
              </button>
            </span>
          )}
        </SettingsRow>

        {/* Prompt body — read-only for presets, editable for customs */}
        <div className="settings-stack settings-stack--tight">
          <textarea
            className={
              editing ? 'settings-textarea settings-textarea--editing' : 'settings-textarea'
            }
            value={editing ? (draftBody ?? '') : selectedBody}
            readOnly={!editing}
            onChange={(e) => editing && setDraftBody(e.target.value)}
            rows={12}
            spellCheck={false}
            aria-label="Routing prompt text"
          />
          {editing && (
            <SettingsRow stack htmlFor="autorouter-prompt-name" label="Name">
              <input
                id="autorouter-prompt-name"
                type="text"
                className="settings-input"
                value={draftName ?? ''}
                onChange={(e) => setDraftName(e.target.value)}
                aria-label="Custom prompt name"
              />
            </SettingsRow>
          )}

          {/* Actions */}
          <div className="settings-controls">
            {active.kind === 'preset' && (
              <button
                type="button"
                className="btn btn--sm"
                disabled={saving}
                onClick={duplicateAndCustomize}
              >
                Duplicate & customize
              </button>
            )}
            {active.kind === 'custom' && !editing && (
              <>
                <button type="button" className="btn btn--sm" onClick={beginEditCustom}>
                  Edit
                </button>
                <button type="button" className="btn btn--sm" onClick={duplicateAndCustomize}>
                  Duplicate
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  disabled={saving}
                  onClick={deleteCustom}
                >
                  Delete
                </button>
              </>
            )}
            {editing && (
              <>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={saving}
                  onClick={saveCustom}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() => {
                    setDraftName(null);
                    setDraftBody(null);
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Routing activity — U9: collapsed by default; the open log made this
            card ~700px tall next to a 240px neighbor. */}
        <details className="settings-disclosure">
          <summary>
            Recent routing activity
            {activityQuery.data !== undefined ? ` · ${activityQuery.data.length}` : ''}
          </summary>
          {activityQuery.isLoading && <span className="settings-hint">Loading…</span>}
          {activityQuery.isError && (
            <span className="settings-hint settings-hint--error">
              Couldn't load —{' '}
              <button
                type="button"
                className="btn btn--xs"
                onClick={() => void activityQuery.refetch()}
              >
                retry
              </button>
            </span>
          )}
          {activityQuery.data && activityQuery.data.length === 0 && (
            <span className="settings-hint">Nothing routed yet.</span>
          )}
          <div className="settings-activity">
            {(activityQuery.data ?? []).map((a) => {
              const undone = a.undoneAt !== null;
              const superseded = a.supersededAt !== null;
              return (
                <div
                  key={a.id}
                  className={
                    undone || superseded
                      ? 'settings-activity__row settings-activity__row--muted'
                      : 'settings-activity__row'
                  }
                >
                  <Icon name="route" size={13} />
                  <span className="settings-activity__main">
                    <strong>{a.noteTitle ?? 'note'}</strong>
                    {' → '}
                    {a.toNotebookTitle ?? '(folder)'}{' '}
                    <span className="settings-activity__meta">
                      ({a.confidence}) {a.reason}
                    </span>
                  </span>
                  {!undone && !superseded && (
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={undo.isPending}
                      title="Move this note back to the Inbox"
                      onClick={() => undo.mutate(a.id)}
                    >
                      Send back to Inbox
                    </button>
                  )}
                  {undone && <span className="settings-activity__meta">sent back</span>}
                  {superseded && !undone && (
                    <span className="settings-activity__meta">moved by you</span>
                  )}
                </div>
              );
            })}
          </div>
        </details>
      </div>
    </SettingsCard>
  );
}
